import { apiDelete, apiGet, apiPost } from './client.js';

export type M360LookupKind = 'auto' | 'imei' | 'm360id' | 'sessionId' | 'serial';

export type M360Security = {
  associatedAccount?: string | null;
  frpState?: boolean | null;
  iosJailbreak?: boolean | null;
  rooted?: boolean | null;
  secureLocked?: boolean | null;
  mdmState?: string | null;
  samsungKnoxWarranty?: string | null;
  deviceOrigin?: string | null;
  simLock?: string | null;
  simStatus?: string | null;
  networkOperator?: string | null;
  blacklist?: string | null;
  simLockIsLocked?: boolean | null;
  simLockCarrier?: string | null;
  oemIsAuthentic?: boolean | null;
  nonOriginalParts?: Array<{ name: string; isOriginal: boolean | null }>;
};

export type M360Battery = {
  healthPercent?: number | null;
  preciseHealthPercent?: number | null;
  estimatedHealthPercent?: number | null;
  cycleCount?: number | null;
  designCapacityMah?: number | null;
  fullChargeCapacityMah?: number | null;
  status?: string | null;
};

export type M360Grading = {
  screen?: number | null;
  back?: number | null;
  phoneCase?: number | null;
  lcd?: number | null;
  secondScreen?: number | null;
  secondLcd?: number | null;
  accessories?: string[] | null;
  comment?: string | null;
};

export type M360DiagnosticRow = {
  id: string;
  ticketId: string;
  ticketDeviceId: string | null;
  diagnosticType: string;
  overallGrade: string | null;
  deviceMake?: string | null;
  deviceModel?: string | null;
  passedTests: number | null;
  failedTests: number | null;
  skippedTests?: number | null;
  totalTests: number | null;
  batteryHealthPercent: number | null;
  batteryCycleCount: number | null;
  batteryStatus: string | null;
  fullResults: Record<string, unknown> | null;
  status: string | null;
  completedAt: string | null;
  createdAt: string;
  reportUrl?: string | null;
  htmlReportUrl?: string | null;
  pdfReportUrl?: string | null;
  username?: string | null;
  connectionTime?: string | null;
  m360id?: string | null;
  sessionId?: string | null;
  suggestedCondition?: 'excellent' | 'good' | 'fair' | 'poor' | 'broken' | null;
  deviceInfo?: {
    imei?: string;
    make?: string;
    model?: string;
    marketingName?: string | null;
    color?: string | null;
  };
  tests?: Array<{
    name: string;
    category: string;
    status: 'pass' | 'fail' | 'skip';
    value?: string;
    details?: string;
  }>;
  battery?: M360Battery;
  security?: M360Security;
  grading?: M360Grading | null;
};

export type M360TicketDiagnostics = {
  pre: M360DiagnosticRow | null;
  post: M360DiagnosticRow | null;
  history: M360DiagnosticRow[];
};

export function getM360Status() {
  return apiGet<{
    configured: boolean;
    apiUrl: string | null;
    keyFormatOk?: boolean;
    keyFormatWarning?: string | null;
    testingMode?: boolean;
  }>('/api/v1/m360/status').then((r) => r.data);
}

export function getTicketM360Diagnostics(ticketId: string) {
  return apiGet<M360TicketDiagnostics>(`/api/v1/m360/diagnostics/ticket/${ticketId}`).then(
    (r) => r.data,
  );
}

export function retrieveM360Diagnostic(body: {
  ticketId: string;
  ticketDeviceId?: string;
  query?: string;
  imei?: string;
  lookupKind?: M360LookupKind;
  diagnosticType: 'pre' | 'post';
}) {
  return apiPost<M360DiagnosticRow>('/api/v1/m360/diagnostics/retrieve', body).then((r) => r.data);
}

export function retrieveM360DiagnosticById(body: {
  ticketId: string;
  ticketDeviceId?: string;
  reportId: string;
  diagnosticType: 'pre' | 'post';
}) {
  return apiPost<M360DiagnosticRow>('/api/v1/m360/diagnostics/retrieve-by-id', body).then(
    (r) => r.data,
  );
}

export function deleteM360Diagnostic(id: string) {
  return apiDelete(`/api/v1/m360/diagnostics/${id}`);
}
